"use client";

import Image from 'next/image';
import Link from "next/link";
import { mutedText, linkText } from "@/lib/typography";
import { useAuth } from "@/context/auth-context";
import { isTutor } from "@/lib/role-utils";
import { JOB_BOARD_ENABLED } from "@/constants/feature-flags";
import { useHasOpenJobs } from "@/hooks/use-has-open-jobs";
import { FOOTER_LEGAL_LINKS, getFooterLinkGroups } from "@/lib/footer-links";
import { AppDownloadLink } from "@/components/app-download-link";
import { ANDROID_PLAY_STORE_URL } from "@/constants/app-store";

export default function Footer(): JSX.Element {
  const { user } = useAuth();
  const hideStudyGroupsForTutor = isTutor(user);
  const hasOpenJobs = useHasOpenJobs();
  const currentYear = new Date().getFullYear();
  const footerGroups = getFooterLinkGroups({
    hideStudyGroups: hideStudyGroupsForTutor,
    jobBoardEnabled: JOB_BOARD_ENABLED,
    hasOpenJobs,
  });

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="professional-container">
        <div className="py-12">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            
            {/* Company Info */}
            <div className="space-y-3">
              <div className="flex items-center">
                <Image src="/main-navigation-logo.png" alt="CertLynx Logo" width={211} height={40} className="h-8 w-auto" />
              </div>
              <p className="font-bold text-gray-900">
                The community for people pursuing professional certifications.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Find study partners, join groups, get expert help, and prepare
                with people in your corner.
              </p>
              <AppDownloadLink
                href={ANDROID_PLAY_STORE_URL}
                store="google_play"
                className="inline-block w-fit"
              >
                <Image
                  src="/google-play-badge.png"
                  alt="Get it on Google Play"
                  width={646}
                  height={250}
                  className="h-16 w-auto"
                />
              </AppDownloadLink>
            </div>

            {footerGroups.map((group) => (
              <div key={group.heading} className="space-y-4">
                <h3 className="text-sm font-bold text-gray-900">{group.heading}</h3>
                <ul className="space-y-2">
                  {group.items.map((item) => (
                    <li key={item.href}>
                      <Link href={item.href} className={linkText}>
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>



          {/* Bottom Bar */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <p className={mutedText}>
                © {currentYear} CertLynx. All rights reserved.
              </p>
              <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-6 gap-y-2">
                {FOOTER_LEGAL_LINKS.map((item) => (
                  <Link key={item.href} href={item.href} className={linkText}>
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
